import { useEffect, useState } from 'react';

import {
  LookupAutocompleteView,
  mapDropdownOptions,
} from '@/shared/ui/molecules/LookupAutocomplete';
import { useDisciplineName, useDisciplineSearch } from '../../../hooks/useDisciplineLookup';

export interface DisciplineAutocompleteProps {
  /** ID da disciplina selecionada; `null` quando nada. */
  readonly value: number | null;
  /** Notifica o novo ID (`null` ao limpar). */
  readonly onChange: (id: number | null) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de disciplina do formulário de questão.
 *
 * Resolve sozinho o rótulo da disciplina já salva (via `exam/discipline-single`),
 * porque o detalhe da questão traz só o `disciplineId` e a busca do dropdown
 * exige 3 caracteres — sem isso o campo abriria vazio na edição.
 *
 * @example
 * ```tsx
 * <DisciplineAutocomplete
 *   value={field.value ?? null}
 *   onChange={field.onChange}
 *   label={t('edit.settings.fields.discipline')}
 *   placeholder={t('edit.settings.placeholders.discipline')}
 * />
 * ```
 */
export function DisciplineAutocomplete({
  value,
  onChange,
  label,
  placeholder,
}: DisciplineAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [inputText, setInputText] = useState('');
  const { options, isQuerySuccess } = useDisciplineSearch(search);
  const resolvedName = useDisciplineName(value);

  // O nome chega de forma assíncrona e depois da montagem; só adota quando o
  // usuário ainda não escolheu outra opção nesta sessão do formulário.
  useEffect(() => {
    if (resolvedName) setInputText(resolvedName);
  }, [resolvedName]);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value !== null ? String(value) : ''}
      selectedLabel={inputText}
      options={mapDropdownOptions(options)}
      isQuerySuccess={isQuerySuccess}
      search={search}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        onChange(id ? Number(id) : null);
        setInputText(itemLabel);
        setSearch('');
      }}
      onClear={() => {
        onChange(null);
        setInputText('');
        setSearch('');
      }}
    />
  );
}
